import { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet } from "../lib/api";
import type {
  OutreachRecipient,
  OutreachReportingResponse,
  OutreachReportingRow,
} from "../lib/types";
import { useScope } from "../scope/ScopeContext";
import { OutreachStatusStrip } from "../components/OutreachStatusStrip";
import {
  customRangeLabel,
  dateWindowOptions,
  lastFullMonth,
  rangeProblem,
  todayInEastern,
  type CustomRange,
} from "../lib/date-windows";
import { CustomRangeFields } from "../components/CustomRangeFields";

/**
 * Outreach reporting — per-rooftop funnel (pulled → sent → clicked → responded →
 * reviews matched) with a group rollup row, satisfaction distribution, and
 * how-found-us breakdown for the current scope + date range.
 */

const RANGE_OPTIONS = [
  ...dateWindowOptions(),
  { days: 365, label: "Last year" },
  { days: 0, label: "All time" },
];

export function OutreachReportingPage() {
  const { focusedDealerId, dealersInFocus } = useScope();
  const [days, setDays] = useState(30);
  // Custom dates replace the preset window when set — DP #36602: Alan Jay runs
  // a monthly employee contest, and the "Team member recognized" column in
  // this export is who customers named. Eastern calendar days, both ends in.
  const [customRange, setCustomRange] = useState<CustomRange | null>(null);
  const rangeBad = customRange ? rangeProblem(customRange) !== null : false;
  const [typeFilter, setTypeFilter] = useState<"" | "sales" | "service">("");
  const [data, setData] = useState<OutreachReportingResponse | null>(null);
  // The per-customer rows behind the totals — the same content as the CSV, on
  // screen. The aggregate answers "how is it going"; this answers "who said
  // what", which is the half a manager actually acts on.
  const [responses, setResponses] = useState<OutreachRecipient[]>([]);
  const [showResponses, setShowResponses] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    // Half-typed or backwards dates: the date panel says what's wrong; don't
    // leave the last range's totals on screen under the new dates.
    if (rangeBad) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (focusedDealerId) params.set("dealerId", focusedDealerId);
      if (typeFilter) params.set("type", typeFilter);
      if (customRange) {
        params.set("from", customRange.from);
        params.set("to", customRange.to);
      } else if (days > 0) {
        params.set("from", new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString());
      }
      const res = await apiGet<OutreachReportingResponse>(
        `/api/outreach/reporting?${params.toString()}`
      );
      setData(res);
    } catch (err) {
      console.error("reporting load failed:", err);
    } finally {
      setLoading(false);
    }
  }, [focusedDealerId, typeFilter, days, customRange, rangeBad]);

  useEffect(() => {
    void load();
  }, [load]);

  // Only people we actually messaged, newest answer first. Same window and
  // filters as the summary above it, so the table and the totals agree.
  const loadResponses = useCallback(async () => {
    if (rangeBad) {
      setResponses([]);
      return;
    }
    try {
      const params = new URLSearchParams({ status: "sent", limit: "300" });
      if (focusedDealerId) params.set("dealerId", focusedDealerId);
      if (typeFilter) params.set("type", typeFilter);
      if (customRange) {
        params.set("from", customRange.from);
        params.set("to", customRange.to);
      } else if (days > 0) params.set("withinDays", String(days));
      const res = await apiGet<{ recipients: OutreachRecipient[] }>(
        `/api/outreach/recipients?${params.toString()}`
      );
      setResponses(
        res.recipients.sort((a, b) =>
          (b.respondedAt ?? b.sentAt ?? "").localeCompare(a.respondedAt ?? a.sentAt ?? "")
        )
      );
    } catch (err) {
      // A detail table must never take the summary down with it.
      console.error("responses load failed:", err);
    }
  }, [focusedDealerId, typeFilter, days, customRange, rangeBad]);

  useEffect(() => {
    if (showResponses) void loadResponses();
  }, [showResponses, loadResponses]);

  // The export mirrors whatever is on screen, so what Don downloads is what he
  // is looking at. A plain link, not a fetch: the session cookie rides along
  // same-origin and the browser handles the file, so there is no blob dance.
  //
  // A plain link can't carry the focus headers apiGet adds, so a group focus
  // used to be dropped and a super_admin got every tenant's file. The stores
  // in view now ride along as dealerId params; the server intersects them
  // with the caller's own scope.
  const exportHref = useMemo(() => {
    const params = new URLSearchParams();
    if (focusedDealerId) params.set("dealerId", focusedDealerId);
    else for (const d of dealersInFocus) params.append("dealerId", d.id);
    if (typeFilter) params.set("type", typeFilter);
    if (customRange) {
      params.set("from", customRange.from);
      params.set("to", customRange.to);
    } else if (days > 0) {
      params.set("from", new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString());
    }
    return `/api/outreach/export.csv?${params.toString()}`;
  }, [focusedDealerId, dealersInFocus, typeFilter, days, customRange]);

  const rollup = useMemo(() => {
    if (!data || data.rows.length === 0) return null;
    const sum = (f: (r: OutreachReportingRow) => number) =>
      data.rows.reduce((n, r) => n + f(r), 0);
    return {
      pulled: sum((r) => r.pulled),
      skippedIneligible: sum((r) => r.skippedIneligible),
      sent: sum((r) => r.sent),
      clicked: sum((r) => r.clicked),
      responded: sum((r) => r.responded),
      reviewClicked: sum((r) => r.reviewClicked),
      privateFeedback: sum((r) => r.privateFeedback),
      reviewsMatched: sum((r) => r.reviewsMatched),
    };
  }, [data]);

  const satisfactionTotal = data?.satisfaction.reduce((n, s) => n + s.count, 0) ?? 0;
  const howFoundTotal = data?.howFoundUs.reduce((n, h) => n + h.count, 0) ?? 0;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-8 sm:py-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Outreach reporting</h1>
          <p className="mt-1 text-sm text-slate-500">
            Ask-to-review funnel per rooftop. "Reviews earned" are public reviews the system
            matched back to a specific ask.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!rangeBad && (
            <a
              href={exportHref}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Download CSV
            </a>
          )}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as "" | "sales" | "service")}
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
          >
            <option value="">Sales + Service</option>
            <option value="sales">Sales</option>
            <option value="service">Service</option>
          </select>
          <select
            value={customRange ? "custom" : days}
            onChange={(e) => {
              if (e.target.value === "custom") {
                if (!customRange) setCustomRange(lastFullMonth(todayInEastern()));
                return;
              }
              setCustomRange(null);
              setDays(Number(e.target.value));
            }}
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
          >
            {RANGE_OPTIONS.map((o) => (
              <option key={o.days} value={o.days}>
                {o.label}
              </option>
            ))}
            <option value="custom">Custom dates…</option>
          </select>
        </div>
      </div>

      {customRange && (
        <div className="mt-3 max-w-xl">
          <CustomRangeFields
            range={customRange}
            onChange={setCustomRange}
            summary={`${customRangeLabel(customRange)}, Eastern time`}
          />
        </div>
      )}

      <OutreachStatusStrip />

      {loading && <div className="mt-6 text-sm text-slate-500">Loading…</div>}

      {!loading && data && (
        <>
          {rollup && (
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8">
              <Stat label="Pulled" value={rollup.pulled} />
              <Stat label="Not asked (screened)" value={rollup.skippedIneligible} />
              <Stat label="Sent" value={rollup.sent} />
              <Stat label="Opened page" value={rollup.clicked} />
              <Stat label="Responded" value={rollup.responded} />
              <Stat label="Clicked review" value={rollup.reviewClicked} />
              <Stat label="Private feedback" value={rollup.privateFeedback} />
              <Stat label="Reviews earned" value={rollup.reviewsMatched} highlight />
            </div>
          )}

          {/* Phone: one card per store × type. */}
          <div className="mt-6 space-y-2 md:hidden">
            {data.rows.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white px-5 py-8 text-center text-sm text-slate-500">
                No outreach activity in this range.
              </div>
            )}
            {data.rows.map((r) => (
              <div
                key={`${r.dealerId}:${r.type}`}
                className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-medium text-slate-800">{r.dealerName}</div>
                  <div className="text-xs capitalize text-slate-500">{r.type}</div>
                </div>
                <dl className="mt-2 grid grid-cols-4 gap-y-2 text-center">
                  {(
                    [
                      ["Pulled", r.pulled],
                      ["Screened", r.skippedIneligible],
                      ["Sent", r.sent],
                      ["Opened", r.clicked],
                      ["Responded", r.responded],
                      ["Avg ★", r.avgSatisfaction ?? "—"],
                      ["Feedback", r.privateFeedback],
                      ["Reviews", r.reviewsMatched],
                    ] as [string, number | string][]
                  ).map(([label, value]) => (
                    <div key={label}>
                      <dd
                        className={`text-sm font-semibold tabular-nums ${
                          label === "Reviews" ? "text-emerald-700" : "text-slate-900"
                        }`}
                      >
                        {value}
                      </dd>
                      <dt className="text-[10px] uppercase tracking-wide text-slate-400">{label}</dt>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>

          <div className="mt-6 hidden overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm md:block">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2">Store</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2 text-right">Pulled</th>
                  <th className="px-3 py-2 text-right">Screened</th>
                  <th className="px-3 py-2 text-right">Sent</th>
                  <th className="px-3 py-2 text-right">Opened</th>
                  <th className="px-3 py-2 text-right">Responded</th>
                  <th className="px-3 py-2 text-right">Avg ★</th>
                  <th className="px-3 py-2 text-right">Feedback</th>
                  <th className="px-3 py-2 text-right">Reviews</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.rows.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-3 py-6 text-center text-slate-500">
                      No outreach activity in this range.
                    </td>
                  </tr>
                )}
                {data.rows.map((r) => (
                  <tr key={`${r.dealerId}:${r.type}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium text-slate-800">{r.dealerName}</td>
                    <td className="px-3 py-2 capitalize text-slate-600">{r.type}</td>
                    <td className="px-3 py-2 text-right">{r.pulled}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{r.skippedIneligible}</td>
                    <td className="px-3 py-2 text-right">{r.sent}</td>
                    <td className="px-3 py-2 text-right">{r.clicked}</td>
                    <td className="px-3 py-2 text-right">{r.responded}</td>
                    <td className="px-3 py-2 text-right">{r.avgSatisfaction ?? "—"}</td>
                    <td className="px-3 py-2 text-right">{r.privateFeedback}</td>
                    <td className="px-3 py-2 text-right font-semibold text-emerald-700">
                      {r.reviewsMatched}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Satisfaction distribution
              </h2>
              <div className="mt-3 space-y-2">
                {[5, 4, 3, 2, 1].map((score) => {
                  const count = data.satisfaction.find((s) => s.score === score)?.count ?? 0;
                  const pct = satisfactionTotal ? Math.round((count / satisfactionTotal) * 100) : 0;
                  return (
                    <div key={score} className="flex items-center gap-2 text-xs">
                      <span className="w-8 shrink-0 text-slate-600">{score}★</span>
                      <div className="h-3 flex-1 overflow-hidden rounded bg-slate-100">
                        <div
                          className={`h-full ${score >= 4 ? "bg-emerald-500" : score === 3 ? "bg-amber-400" : "bg-rose-500"}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="w-14 shrink-0 text-right text-slate-500">
                        {count} ({pct}%)
                      </span>
                    </div>
                  );
                })}
                {satisfactionTotal === 0 && (
                  <p className="text-xs text-slate-500">No survey responses yet.</p>
                )}
              </div>
            </section>

            <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                How customers found us
              </h2>
              <div className="mt-3 space-y-2">
                {data.howFoundUs.slice(0, 8).map((h) => {
                  const pct = howFoundTotal ? Math.round((h.count / howFoundTotal) * 100) : 0;
                  return (
                    <div key={h.answer} className="flex items-center gap-2 text-xs">
                      <span className="w-36 shrink-0 truncate text-slate-600">{h.answer}</span>
                      <div className="h-3 flex-1 overflow-hidden rounded bg-slate-100">
                        <div className="h-full bg-sky-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-14 shrink-0 text-right text-slate-500">
                        {h.count} ({pct}%)
                      </span>
                    </div>
                  );
                })}
                {howFoundTotal === 0 && (
                  <p className="text-xs text-slate-500">No survey responses yet.</p>
                )}
              </div>
            </section>
          </div>

          <section className="mt-6 rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
              <div>
                <h2 className="text-sm font-semibold text-slate-900">Who said what</h2>
                <p className="mt-0.5 text-xs text-slate-500">
                  Every customer we messaged in this window — the same detail as the CSV, including
                  the private notes to a manager and the people your customers named.
                </p>
              </div>
              <button
                onClick={() => setShowResponses((v) => !v)}
                className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                {showResponses ? "Hide" : "Show responses"}
              </button>
            </div>

            {showResponses && (
              <ul className="divide-y divide-slate-100 border-t border-slate-200 md:hidden">
                {responses.map((r) => {
                  const answered =
                    r.respondedAt === null
                      ? r.firstClickedAt
                        ? "opened, no answer"
                        : "no reply yet"
                      : r.routedTo === "private_feedback"
                        ? "No"
                        : "Yes";
                  return (
                    <li key={r.id} className="px-4 py-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <a
                          href={`/outreach/recipients/${r.id}`}
                          className="font-medium text-slate-900 underline-offset-2 hover:underline"
                        >
                          {[r.firstName, r.lastName].filter(Boolean).join(" ") || "—"}
                        </a>
                        <span
                          className={
                            answered === "Yes"
                              ? "rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"
                              : answered === "No"
                                ? "rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700"
                                : "text-xs text-slate-400"
                          }
                        >
                          {answered}
                        </span>
                      </div>
                      <div className="mt-0.5 text-xs text-slate-500">
                        {r.dealerName} · {r.type}
                        {r.sentAt ? ` · sent ${new Date(r.sentAt).toLocaleDateString()}` : ""}
                      </div>
                      {(r.howFoundUs || r.teamMemberRecognition) && (
                        <div className="mt-1 text-xs text-slate-600">
                          {r.howFoundUs ? `Brought them in: ${r.howFoundUs}` : ""}
                          {r.howFoundUs && r.teamMemberRecognition ? " · " : ""}
                          {r.teamMemberRecognition ? `Recognised: ${r.teamMemberRecognition}` : ""}
                        </div>
                      )}
                      {r.privateFeedback && (
                        <div className="mt-1 text-xs italic text-slate-700">“{r.privateFeedback}”</div>
                      )}
                    </li>
                  );
                })}
                {responses.length === 0 && (
                  <li className="px-4 py-8 text-center text-sm text-slate-500">
                    Nobody has been messaged in this window yet.
                  </li>
                )}
              </ul>
            )}
            {showResponses && (
              <div className="hidden overflow-x-auto border-t border-slate-200 md:block">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                      <th className="px-4 py-2 font-medium">Customer</th>
                      <th className="px-4 py-2 font-medium">Store</th>
                      <th className="px-4 py-2 font-medium">Sent</th>
                      <th className="px-4 py-2 font-medium">Answered</th>
                      <th className="px-4 py-2 font-medium">Brought them in</th>
                      <th className="px-4 py-2 font-medium">Recognised</th>
                      <th className="px-4 py-2 font-medium">Note to manager</th>
                    </tr>
                  </thead>
                  <tbody>
                    {responses.map((r) => {
                      const answered =
                        r.respondedAt === null
                          ? r.firstClickedAt
                            ? "opened, no answer"
                            : "no reply yet"
                          : r.routedTo === "private_feedback"
                            ? "No"
                            : "Yes";
                      return (
                        <tr key={r.id} className="border-t border-slate-100 align-top">
                          <td className="px-4 py-2">
                            <a
                              href={`/outreach/recipients/${r.id}`}
                              className="font-medium text-slate-900 underline-offset-2 hover:underline"
                            >
                              {[r.firstName, r.lastName].filter(Boolean).join(" ") || "—"}
                            </a>
                            <div className="text-[11px] text-slate-400">{r.email ?? r.phone ?? ""}</div>
                          </td>
                          <td className="px-4 py-2 text-xs text-slate-600">
                            {r.dealerName}
                            <div className="text-[11px] text-slate-400">{r.type}</div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-xs text-slate-500">
                            {r.sentAt ? new Date(r.sentAt).toLocaleDateString() : "—"}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-xs">
                            <span
                              className={
                                answered === "Yes"
                                  ? "rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700"
                                  : answered === "No"
                                    ? "rounded-full bg-rose-50 px-2 py-0.5 font-medium text-rose-700"
                                    : "text-slate-400"
                              }
                            >
                              {answered}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-xs text-slate-600">{r.howFoundUs ?? "—"}</td>
                          <td className="px-4 py-2 text-xs text-slate-600">
                            {r.teamMemberRecognition ?? "—"}
                          </td>
                          <td className="px-4 py-2 text-xs text-slate-700">
                            {r.privateFeedback ? (
                              <span className="italic">“{r.privateFeedback}”</span>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {responses.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">
                          Nobody has been messaged in this window yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div
      className={`rounded-xl border p-3 shadow-sm ${
        highlight ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"
      }`}
    >
      <div className={`text-xl font-semibold ${highlight ? "text-emerald-700" : "text-slate-900"}`}>
        {value.toLocaleString()}
      </div>
      <div className="mt-0.5 text-[11px] text-slate-500">{label}</div>
    </div>
  );
}
